import { toast } from 'sonner';

import { registerActionHandler } from '../action-registry';
import { getSmallAppRestToken } from '../get-smallapp-rest-token';
import { getNested } from '../nested-keys';
import { buildSmallAppUrl } from '../smallapp-url';

/** Shape of a successful `POST {apiUrl}asset/project-info` answer (monorepo REST). */
interface ProjectInfoResponse {
  projectId?: string;
  workspaceId?: string;
  name?: string;
  urlSlug?: string;
  visibility?: string;
  publicUrl?: string;
  lastDeployedAt?: string;
}

/**
 * Port of the legacy Form's `pickErrorText` (form.tsx:65-81): prefers a non-empty STRING
 * `error`, then a non-empty string `message`, else `''`. Explicitly never falls through to a bare
 * numeric `error` (e.g. `500`), which the old `error?.response?.error || …` chain could toast.
 */
export function pickErrorText(body: unknown): string {
  if (!body || typeof body !== 'object') return '';
  const { error, message } = body as { error?: unknown; message?: unknown };
  if (typeof error === 'string' && error.trim()) return error;
  if (typeof message === 'string' && message.trim()) return message;
  return '';
}

/**
 * `syncOptidevProjectInfo` — the legacy OptiDev Form's "Sync project info" button
 * (`handleSyncProjectInfo`) plus its submit-time auto-resolve, as a manifest field action on
 * `meta.url`.
 *
 * Resolves `projectId` / `workspaceId` / canonical `publicUrl` for the entered published URL via
 * the monorepo REST's Firebase-authed `POST asset/project-info` (same transport as the engine's
 * `api.onLoad` / `api.onSave` FE-direct calls: `getSmallAppRestToken` + `buildSmallAppUrl`).
 * ⛔ HANDLER mode, not `action.endpoint`: `smallappProxyAction` needs an already-saved asset and
 * mints a service token the Firebase-authed route rejects — it cannot run on CREATE.
 *
 * Triggers:
 *  - `manual` (button click) — always calls the REST; toasts success / failure.
 *  - `isSaveCheck` (runOnSave) — legacy parity: auto-syncs at submit only when the ids are MISSING;
 *    silent on success, returns `{ ok: false, reason }` so ConfigEngine ABORTS the save (the REST's
 *    `asset/save` re-checks server-side as well).
 */
/**
 * A URL the user plausibly finished entering (a paste, or typing that paused): http(s) scheme and a
 * host with a dot and a 2+ letter TLD. Auto-sync on paste reports its error only for such a value —
 * otherwise a half-typed URL would toast on every pause — and the Sync glyph is locked for that value
 * after a failure, so without the toast the user would never learn why.
 */
export function looksLikeCompleteUrl(value: string): boolean {
  try {
    const parsed = new URL(value.trim());
    return /^https?:$/.test(parsed.protocol) && /\.[a-z]{2,}$/i.test(parsed.hostname);
  } catch {
    return false;
  }
}

registerActionHandler(
  'syncOptidevProjectInfo',
  async ({ values, form, appMeta, apolloClient, t, isSaveCheck, trigger }) => {
    const quiet = !!isSaveCheck || (trigger != null && trigger !== 'manual');
    // Auto-sync on paste (`runOnChange`): errors stay silent while the user types, but a success is
    // announced once, like a manual click. The pre-save resolve stays fully silent (legacy parity).
    const announceSuccess =
      !isSaveCheck && (trigger == null || trigger === 'manual' || trigger === 'change');

    const url = String(getNested(values, 'meta.url') ?? '').trim();
    // Auto-sync on paste: the glyph locks after a failed run for this URL, so a finished-looking URL
    // must say why (e.g. "Project not found"); a half-typed one stays silent.
    const announceAutoSyncError = trigger === 'change' && looksLikeCompleteUrl(url);
    const currentUrl = () =>
      String((form?.getValues?.('meta.url' as never) as unknown) ?? '').trim();
    const isStale = () => !!url && !!form?.getValues && currentUrl() !== url;
    // A URL that does not resolve must not keep the PREVIOUS project's ids: with them in place the
    // pre-save check would accept the save and store the new URL against the old project.
    const clearResolvedIds = () => {
      if (isSaveCheck) return;
      form?.setValue('meta.projectId' as never, '' as never, { shouldDirty: true });
      form?.setValue('meta.workspaceId' as never, '' as never, { shouldDirty: true });
    };
    const projectId = String(getNested(values, 'meta.projectId') ?? '').trim();
    const workspaceId = String(getNested(values, 'meta.workspaceId') ?? '').trim();

    if (!url && !projectId) {
      const reason = t('Please enter a URL first');
      if (!quiet) toast.error(reason);
      // Manual click: nothing else to do. Pre-save: `meta.url` is required so this cannot be
      // reached, but never let an empty resolve pass the gate.
      return isSaveCheck ? { ok: false, reason } : undefined;
    }

    // Legacy submit-flow parity: only resolve at save when the ids are missing — an already-synced
    // asset saves without a second round trip.
    if (isSaveCheck && projectId && workspaceId) return { ok: true };

    const apiUrl = appMeta?.apiUrl;
    if (!apiUrl) {
      // Misconfigured AppMeta — an ops problem, not the user's. `asset/save` re-checks anyway.
      if (!quiet) toast.error(t('Failed to sync project info'));
      return;
    }

    let response: Response;
    let body: unknown = null;
    try {
      // Share-link-aware token (refreshes the shareLinkObjectId claim on /share/* pages).
      const token = await getSmallAppRestToken(apolloClient);
      response = await fetch(buildSmallAppUrl(apiUrl, 'asset/project-info'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        // The URL is the source of truth when there is one: the platform resolves `projectId` FIRST,
        // so sending the previous project's id with a newly pasted URL returned the OLD project and
        // the form snapped back to its URL. The id is only a fallback for a row that has no URL.
        body: JSON.stringify(url ? { url } : { projectId }),
      });
      try {
        body = await response.json();
      } catch {
        body = null;
      }
    } catch {
      if (isStale()) return { ok: false, reason: 'stale' };
      const reason = t('Failed to sync project info');
      clearResolvedIds();
      if (!quiet || announceAutoSyncError) toast.error(reason);
      return { ok: false, reason };
    }

    // The user changed the URL while this request was in flight: its answer belongs to a value that
    // is no longer in the box — never write it back (or toast about it).
    if (isStale()) return { ok: false, reason: 'stale' };

    if (!response.ok) {
      const reason =
        pickErrorText(body) ||
        (response.status === 404 ? t('Project not found') : t('Failed to sync project info'));
      clearResolvedIds();
      if (!quiet || announceAutoSyncError) toast.error(reason);
      return { ok: false, reason };
    }

    const info = (body ?? {}) as ProjectInfoResponse;
    // Dotted keys: RHF's Path typing for Record<string, unknown> collapses to `undefined`, so the
    // engine's own nested writes cast (useResetOn / useDisplayFromOptions precedent).
    form?.setValue('meta.projectId' as never, String(info.projectId ?? '') as never, {
      shouldDirty: true,
    });
    form?.setValue('meta.workspaceId' as never, String(info.workspaceId ?? '') as never, {
      shouldDirty: true,
    });
    // Adopt the canonical publicUrl (legacy parity) — only when the platform actually returned one.
    if (typeof info.publicUrl === 'string' && info.publicUrl.trim()) {
      form?.setValue('meta.url' as never, info.publicUrl as never, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    // Improvement over Gen2 (Duy 2026-10-06): an empty Name takes the project name, the same name
    // OptiDev's own "Publish to OptiSigns" wizard gives its assets. Never overwrites a typed name.
    const currentName = String(getNested(values, 'originalFileName') ?? '').trim();
    if (!currentName && typeof info.name === 'string' && info.name.trim()) {
      form?.setValue('originalFileName' as never, info.name.trim() as never, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    if (announceSuccess) toast.success(t('Project info synced successfully'));
    return { ok: true };
  },
);
